import React, { Component } from 'react';
import { NavLink } from 'react-router-dom';
import moment from 'moment';
// @ts-ignore
import ReactTable from 'react-table';
import ActionButton from '../../common/ActionButton';
import Tooltip from '../../common/Tooltip';
import ETACell from '../../common/ETACell';
import RefreshButton from '../../common/refreshbutton';
import DateDropDown from '../common/datedropdown';
import { ROOT_URL_V2, SERVER_URL } from '../../../config';
import EtaDropDown from './etaDropDown';
import { withPushFiltersToUrl, withPutUrlSearchFiltersToProps } from '../common/decorators';
import { getCountDownTimer } from './utils';
import BookingStartTimeWidget from '../bookings/BookingStartTimeWidget';
import BookingEndTimeWidget from '../bookings/BookingEndTimeWidget';
import history from '../../../history';
import SearchBar from '../common/SearchBar';
import { TJobItem, TPageNotification, TUser } from '../../../types';
import { getTrackingUrl } from '../../../epics/api';

type TRow = { original: TJobItem; value: string }

type TTrackingTableProps = {
    user: TUser;
    data: TJobItem[];
    pages: number;
    showPaginationBottom: boolean;
    loading: boolean;
    notification: TPageNotification;
    currentPage: number;
    onFilterChange: () => void;
    onPageChange: () => void;
    onSortedChange: () => void;
    reloadData: () => void;
    searchValue: string;
    setTable: (table: typeof ReactTable) => void;
}

function getTrackingTableColumns(self: {props: TTrackingTableProps}, user: TUser) {
    return [
        {
            Header: 'Ref.',
            accessor: 'reference_number',
            headerClassName: 'ref-margin-left small-col',
            className: 'ref-margin-left small-col',
            id: 'reference_number',
            sortable: false,
            Cell: (row: TRow) => (
                <a href={`${SERVER_URL}/job/${row.original.id}`}>
                    {row.value}
                    {row.original.additional_services &&
                        <Tooltip tooltip={<span>Multi-sector job</span>}>
                            <i className="icon icon-check-double-22 margin-5-px" />
                        </Tooltip>
                    }
                </a>
            ),
        },
        {
            Header: 'Name',
            accessor: 'name',
            Cell: (row: TRow) => (
                <NavLink
                    to={`/operations/freelancer/${
                        row.original.booking
                            ? row.original.booking.freelancer_detail.id
                            : ''
                    }`}
                >
                    <div>
                        {row.original.booking
                            ? row.original.booking.freelancer_detail.full_name
                            : ''}
                    </div>
                </NavLink>
            ),
        },
        {
            Header: 'Service',
            accessor: 'service_role',
            sortable: false,
            Cell: (row: TRow) => {
                const parentServiceRole = row.original.role === 'MX'
                    ? 'Hospitality General'
                    : row.original.service_role;

                const childServiceRoles = row.original.additional_services
                    ? `; ${row.original.additional_services.map(childJob => 
                        childJob.role === 'MX'
                            ? 'Hospitality General'
                            : childJob.service_role
                    ).join('; ')}`
                    : '';

                const serviceRole = <span>{parentServiceRole}{childServiceRoles}</span>;

                return <Tooltip tooltip={serviceRole}>{serviceRole}</Tooltip>;
            },
        },
        {
            Header: 'Client',
            accessor: 'client_full.company_name',
            sortable: false,
            show: !user || user.isAgent,
        },
        {
            Header: 'Location',
            accessor: 'postcode',
            headerClassName: 'small-col',
            className: 'small-col',
        },
        {
            Header: 'Departure',
            headerClassName: 'small-col',
            className: 'small-col',
            Cell: (row: TRow) => {
                const departure = row.original.booking
                    ? row.original.booking.departure
                    : null;
                if (departure) {
                    return <span>{moment(departure.clocking_time).format('HH:mm')}</span>;
                }

                const passedStartTime = moment() > moment(row.original.start_datetime);
                if (passedStartTime) {
                    return <Tooltip tooltip="The freelancer did not confirm departure"><span>N/A</span></Tooltip>;
                }

                return null;
            },
        },
        {
            Header: 'Start time',
            accessor: 'start_datetime',
            headerClassName: 'small-col',
            className: 'small-col',
            Cell: (row: TRow) => (
                <BookingStartTimeWidget
                    job={row.original}
                    notification={self.props.notification}
                    history={history}
                    table={self}
                    enableModal={false}
                    enableLinkToModal={false}
                    isPreviewMode={true}
                />
            ),
        },
        {
            Header: 'End time',
            accessor: 'end_datetime',
            headerClassName: 'small-col',
            className: 'small-col',
            Cell: (row: TRow) => (
                <BookingEndTimeWidget
                    job={row.original}
                    notification={self.props.notification}
                    history={history}
                    table={self}
                    enableModal={false}
                    enableLinkToModal={false}
                    isPreviewMode={true}
                />
            ),
        },
        {
            Header: 'Start vs. ETA',
            accessor: 'time',
            headerClassName: 'small-col',
            className: 'small-col',
            Cell: (row: TRow) => {
                const clockin = row.original.booking
                    ? row.original.booking.clock_in
                    : null;
                if (
                    row.original.start_datetime &&
                    row.original.booking &&
                    row.original.booking.eta
                ) {
                    const etavstart = getCountDownTimer(
                        row.original.start_datetime,
                        row.original.booking.eta,
                    );
                    const etarefresh = getCountDownTimer(
                        new Date(),
                        row.original.booking.eta_updated,
                    );
                    const refresh = etarefresh
                        ? `${etarefresh[1]} ${etarefresh[0]} ago`
                        : 'N/A';
                    if (row.original.booking.first_seen) {
                        return (
                            <Tooltip
                                tooltip={
                                    <span>
                                        Arrived at{' '}
                                        {moment(
                                            row.original.booking.first_seen,
                                        ).format('HH:mm')}{' '}
                                        <br />
                                        Last update {refresh}
                                    </span>
                                }
                            >
                                <span>Arrived</span>
                            </Tooltip>
                        );
                    }
                    return (
                        <Tooltip tooltip={<span>Last update {refresh}</span>}>
                            <span>
                                <ETACell
                                    job={row.original}
                                    value={etavstart}
                                />
                            </span>
                        </Tooltip>
                    );
                }
                if (clockin) {
                    return (
                        <Tooltip tooltip={<span>Clocked in (No GPS)</span>}>
                            <span>Arrived</span>
                        </Tooltip>
                    );
                }
                return (
                    <div>
                        <div>N/A</div>
                    </div>
                );
            },
        },
    ];
}

// @ts-ignore
@withPushFiltersToUrl
// @ts-ignore
@withPutUrlSearchFiltersToProps
class TrackingTable extends Component<TTrackingTableProps> {

    getTrackingTableColumns = () =>
        getTrackingTableColumns(this, this.props.user);

    // eslint-disable-next-line  arrow-body-style
    getTdProps = (state: any, row: TRow) => {
        return {
            onDoubleClick: () => {
                if (row.original.booking && row.original.booking) {
                    this.goToFreelancerTrackingMap(row.original.booking.id);
                } else {
                    this.props.notification.error(
                        'Error',
                        `Thier is no GPS data available for  ${row.original.booking.freelancer_detail.full_name}`,
                    );
                }
            },
        };
    };

    goToFreelancerTrackingMap = (id: number) => {
        history.push(`/operations/locationtracking/map/${id}`);
    };

    getPdfDownloadUrl = () => {
        const searchParams = new URLSearchParams(history.location.search);
        return `${ROOT_URL_V2}${getTrackingUrl(searchParams)}&format=pdf`;
    }

    render() {
        const currentUrlParams = new URLSearchParams(history.location.search);
        const pdfDownloadUrl = this.getPdfDownloadUrl();

        return (
            <div className="table-container">
                <div className="table-filter-container">
                    <EtaDropDown history={history} />
                    <DateDropDown
                        onFilterChange={this.props.onFilterChange}
                        history={history}
                    />
                    <SearchBar
                        filterKey="search"
                        onFilterChange={this.props.onFilterChange}
                        currentUrlParams={currentUrlParams}
                        value={this.props.searchValue}
                    />
                    <div className="table-action-bar">
                        <RefreshButton reloadData={this.props.reloadData} />

                        <ActionButton
                            iconClass="icon-navigation-next-4"
                            tooltip="Export as PDF"
                            // eslint-disable-next-line no-restricted-globals
                            onAction={() => {location.href = pdfDownloadUrl;}}
                        />

                        <div className="grouped-buttons">
                            <ActionButton
                                active={true}
                                tooltip="List view"
                                iconClass="icon-view-list"
                            />
                            <ActionButton
                                tooltip="Map view"
                                iconClass="icon-map-pin-1"
                                onAction={() =>
                                    history.push(
                                        '/operations/locationtracking/map/',
                                    )
                                }
                            />
                        </div>
                    </div>
                </div>
                <ReactTable
                    page={this.props.currentPage}
                    pageSize={0}
                    data={this.props.data}
                    pages={this.props.pages}
                    columns={this.getTrackingTableColumns()}
                    showPaginationBottom={this.props.showPaginationBottom}
                    className="table-content tracking-table"
                    loading={this.props.loading}
                    showPageSizeOptions={false}
                    manual={true}
                    getTrProps={this.getTdProps}
                    ref={(table: typeof ReactTable) => {
                        // @ts-ignore
                        this.table = table;
                        return this.props.setTable(table);
                    }}
                    onPageChange={this.props.onPageChange}
                    onSortedChange={this.props.onSortedChange}
                />
            </div>
        );
    }
}

export { TrackingTable, getTrackingTableColumns };
